import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PricingModel = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plan = {
    id: 'pro',
    name: 'Pro',
    price: 249,
    period: 'month',
    description: 'Everything you need for professional AI-powered journalism',
    features: [
      { text: 'Unlimited AI-generated articles', included: true },
      { text: 'Advanced fact-checking with sources', included: true },
      { text: 'Real-time bias detection & monitoring', included: true },
      { text: 'Priority support & dedicated assistance', included: true },
      { text: 'Export to multiple formats (PDF, DOCX, HTML)', included: true },
      { text: 'Advanced analytics dashboard', included: true },
      { text: 'Team collaboration tools', included: true },
      { text: 'API access for integrations', included: true },
      { text: 'Custom AI model training', included: true },
      { text: 'White-label options', included: true }
    ],
    color: 'primary',
    gradient: 'from-primary to-secondary'
  };

  const handleSelectPlan = () => {
    navigate('/checkout', { state: { plan, billingCycle } });
  };

  const finalPrice = billingCycle === 'yearly' ? Math.round(plan.price * 0.8) : plan.price;
  const yearlyTotal = finalPrice * 12;
  const monthlySavings = billingCycle === 'yearly' ? (plan.price - finalPrice) * 12 : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-primary rounded-2xl mb-4">
          <Icon name="Crown" size={32} color="white" />
        </div>
        <h1 className="text-4xl font-heading font-bold text-foreground mb-4">
          Upgrade to Pro
        </h1>
        <p className="text-lg text-muted-foreground mb-8">
          Unlock the full power of AI-driven journalism
        </p>

        {/* Billing Toggle */}
        <div className="inline-flex items-center bg-muted rounded-lg p-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              billingCycle === 'monthly'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              billingCycle === 'yearly'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Yearly
            <span className="ml-2 text-xs text-success font-semibold">Save 20%</span>
          </button>
        </div>
      </div>

      {/* Pricing Card */}
      <div className="max-w-2xl mx-auto">
        <div className="relative bg-card rounded-2xl border-2 border-primary shadow-elevated p-8">
          {/* Popular Badge */}
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <div className={`bg-gradient-to-r ${plan.gradient} text-white px-6 py-2 rounded-full text-sm font-semibold flex items-center space-x-2 shadow-lg`}>
              <Icon name="Star" size={16} />
              <span>Best Value</span>
            </div>
          </div>

          <div className="text-center mb-8">
            {/* Plan Name */}
            <h2 className="text-3xl font-heading font-bold text-foreground mb-2">
              {plan.name} Plan
            </h2>
            <p className="text-muted-foreground mb-6">
              {plan.description}
            </p>

            {/* Price */}
            <div className="mb-6">
              <div className="flex items-baseline justify-center">
                <span className="text-6xl font-bold text-foreground">₹{finalPrice}</span>
                <span className="text-xl text-muted-foreground ml-2">/{plan.period}</span>
              </div>
              {billingCycle === 'yearly' && (
                <div className="mt-3 space-y-1">
                  <p className="text-lg text-success font-semibold">
                    ₹{yearlyTotal} billed yearly
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Save ₹{monthlySavings} per year compared to monthly
                  </p>
                </div>
              )}
            </div>

            {/* CTA Button */}
            <Button
              onClick={handleSelectPlan}
              className={`w-full bg-gradient-to-r ${plan.gradient} hover:opacity-90 text-white border-0 text-lg py-6`}
              iconName="ArrowRight"
              iconPosition="right"
            >
              Get Started Now
            </Button>

            <p className="text-xs text-muted-foreground mt-3">
              7-day money-back guarantee • Cancel anytime
            </p>
          </div>

          {/* Features List */}
          <div className="border-t border-border pt-8">
            <h3 className="text-lg font-semibold text-foreground mb-4 text-center">
              Everything Included:
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              {plan.features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3"
                >
                  <Icon
                    name="Check"
                    size={20}
                    color="var(--color-success)"
                    className="flex-shrink-0 mt-0.5"
                  />
                  <span className="text-sm text-foreground">
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
        <div className="p-6 bg-card rounded-xl border border-border">
          <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="Shield" size={24} color="var(--color-success)" />
          </div>
          <h4 className="font-semibold text-foreground mb-2">Secure & Safe</h4>
          <p className="text-sm text-muted-foreground">
            Bank-level encryption and secure payment processing
          </p>
        </div>
        <div className="p-6 bg-card rounded-xl border border-border">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="RefreshCw" size={24} color="var(--color-primary)" />
          </div>
          <h4 className="font-semibold text-foreground mb-2">Cancel Anytime</h4>
          <p className="text-sm text-muted-foreground">
            No long-term commitment. Cancel your subscription anytime
          </p>
        </div>
        <div className="p-6 bg-card rounded-xl border border-border">
          <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="Headphones" size={24} color="var(--color-accent)" />
          </div>
          <h4 className="font-semibold text-foreground mb-2">24/7 Support</h4>
          <p className="text-sm text-muted-foreground">
            Priority support available whenever you need help
          </p>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mt-12 bg-card rounded-xl border border-border p-8">
        <h2 className="text-2xl font-heading font-bold text-foreground mb-6 text-center">
          Frequently Asked Questions
        </h2>
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-foreground mb-2 flex items-center space-x-2">
              <Icon name="HelpCircle" size={16} color="var(--color-primary)" />
              <span>What payment methods do you accept?</span>
            </h4>
            <p className="text-sm text-muted-foreground pl-6">
              We accept all major credit cards, debit cards, UPI, and net banking through Stripe's secure payment gateway.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-2 flex items-center space-x-2">
              <Icon name="HelpCircle" size={16} color="var(--color-primary)" />
              <span>Is there a free trial?</span>
            </h4>
            <p className="text-sm text-muted-foreground pl-6">
              Yes! You get a 7-day money-back guarantee. If you're not satisfied, we'll refund your payment in full.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-2 flex items-center space-x-2">
              <Icon name="HelpCircle" size={16} color="var(--color-primary)" />
              <span>Can I switch between monthly and yearly billing?</span>
            </h4>
            <p className="text-sm text-muted-foreground pl-6">
              Yes, you can upgrade to yearly billing anytime to get the 20% discount. The change takes effect on your next billing cycle.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-2 flex items-center space-x-2">
              <Icon name="HelpCircle" size={16} color="var(--color-primary)" />
              <span>How do I cancel my subscription?</span>
            </h4>
            <p className="text-sm text-muted-foreground pl-6">
              You can cancel anytime from your profile settings. Your access continues until the end of your billing period.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingModel;